/**
 * deleteTimeEntry — DELETEs a CallLog (hard delete of a manual entry,
 * not a void). Depends on the parent visit having synced.
 *
 * Ordering hazard (#1987): sync() resolves the server calllogid by looking
 * up the parent visit_task_sessions row by uuid. So applyLocal must NOT
 * remove that row — if it did, sync() would find no row, short-circuit to a
 * phantom "success", and never issue the server DELETE (the bug that bites
 * server-sourced rows adopted via adoptServerCallLog, which have no other
 * carrier for the calllogid). Instead applyLocal stamps the row
 * `pendingDelete` + status 'deleted' so the local-first renderers hide it
 * immediately while the calllogid survives for sync(); reconcile() removes
 * the row for good once the server confirms the DELETE.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, remove, put, getAll } from '../../utils/indexedDB';
import { emit } from '../../utils/eventLog';
import {
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveCallLogUrl,
} from '../httpHelpers';

export interface DeleteTimeEntryInput {
  visitLocalId: string;
}

export interface DeleteTimeEntryResponse {
  success?: boolean;
}

export const deleteTimeEntryOp: WriteOp<
  DeleteTimeEntryInput,
  DeleteTimeEntryInput,
  DeleteTimeEntryResponse
> = {
  kind: 'deleteTimeEntry',

  async applyLocal(input) {
    // Mark (don't remove) the parent row: keep its calllogid alive for sync()
    // to resolve the DELETE URL, while flagging it so the local-first
    // renderers hide it immediately. reconcile() removes it after the server
    // confirms. If the row doesn't exist (visit never persisted locally),
    // there's nothing to flag — sync() handles that case.
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === input.visitLocalId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, {
      ...match,
      pendingDelete: true,
      status: 'deleted',
    });

    // Event stream (#1950 P2): a time-record change → time_edited (action delete).
    try {
      await emit({
        event_type: 'time_edited',
        store_id: (match.storeId as number) ?? null,
        project_id: (match.projectId as number) ?? null,
        calllog_id: (match.callLogId as number) ?? null,
        payload: { local_id: input.visitLocalId, action: 'delete' },
      });
    } catch {
      /* event emit is best-effort */
    }
  },

  serialize(input, localId) {
    return {
      action: 'deleteTimeEntry',
      endpoint: '/api/projects/calllog/{calllogid}/',
      method: 'DELETE',
      payload: input,
      priority: 50,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as DeleteTimeEntryInput;
    // The row may have been removed locally already, so we can't rely on
    // visit_task_sessions to still hold callLogId. Try; if missing, treat
    // as success (server may already lack the row too, in which case we
    // are idempotent). For now look up before removing in applyLocal.
    const urlOrShortCircuit = await resolveCallLogUrl(row, input.visitLocalId);
    if (typeof urlOrShortCircuit !== 'string') {
      // If the parent was never synced and is now locally gone, there is
      // no server CallLog to delete — succeed silently.
      if (urlOrShortCircuit.errorMessage === 'Parent visit has not synced yet') {
        const stillExists = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<{
          uuid?: string;
        }>;
        if (!stillExists.find((r) => r.uuid === input.visitLocalId)) {
          return { status: 'success', retriable: false };
        }
      }
      return urlOrShortCircuit;
    }
    const url = urlOrShortCircuit;

    let response: Response;
    try {
      response = await fetch(url, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    // 404 on DELETE is fine — row already gone (idempotent).
    if (response.status === 404) return { status: 'success', retriable: false };

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile(row) {
    // DELETE confirmed by the server: now drop the local parent row for good.
    const input = row.payload as DeleteTimeEntryInput;
    await remove(STORES.VISIT_TASK_SESSIONS, input.visitLocalId);
  },

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type DeleteTimeEntryOp = typeof deleteTimeEntryOp;
